import { tramoForPoints, TRAMOS, type Tramo } from '@selflevel/domain';

/**
 * LA ARITMÉTICA DEL CAMINO, fuera del componente.
 *
 * Dos cosas que no se ven y que, si se rompen, no lo nota nadie hasta que se
 * mira muy de cerca: cuántos hitos se pintan, y la profundidad de cada fila.
 * La segunda tiene un invariante frágil —ver `profundidad`— que es justo la
 * clase de cosa que un test sostiene y una revisión de código no.
 */

export type Profundidad = {
  /** Borde de arriba de la fila. 0 es lo más lejos. */
  z0: number;
  /** Borde de abajo. 1 es lo más cerca, a tus pies. */
  z1: number;
};

/**
 * Cuántos hitos se pintan.
 *
 * Plegado: los alcanzados más los cinco siguientes. Corto, en el flujo de la
 * página e imposible de convertir en una trampa de scroll. Desplegado: todos.
 * Sin hito siguiente —camino terminado— se pintan todos igualmente: no hay
 * futuro que recortar.
 */
export function hastaHito(siguienteN: number | null, abierto: boolean, total: number): number {
  if (abierto || siguienteN === null) return total;
  return Math.min(total, siguienteN + 5);
}

/**
 * La profundidad de la fila del hito `n`, con `1` el primero del camino.
 *
 * EL INVARIANTE: el borde de abajo de una fila es EXACTAMENTE el de arriba de
 * la que tiene debajo. De eso depende que los trapecios de cada fila se suelden
 * en una sola rampa en fuga; si se separan aunque sea por redondeo, aparecen
 * escalones en los bordes y la rampa deja de leerse como una rampa.
 *
 * Y SALE DEL CATÁLOGO ENTERO, no de cuántas filas se estén pintando. Dividir
 * por las pintadas hacía que al desplegar las filas que ya veías cambiaran de
 * profundidad —y con ella de ancho y de alto—, o sea que abrir el camino
 * re-proporcionaba de golpe el trozo que estabas mirando. Contra el catálogo,
 * la profundidad de un hito es la que le toca por dónde cae y no cambia nunca.
 */
export function profundidad(n: number, total: number): Profundidad {
  return { z0: (total - n) / total, z1: (total - n + 1) / total };
}

/** El punto medio, que es lo que usan la escala de la pieza y el retardo de la
 *  cascada: una fila entera tiene una sola distancia al ojo. */
export function profundidadMedia(n: number, total: number): number {
  const { z0, z1 } = profundidad(n, total);
  return (z0 + z1) / 2;
}

/**
 * EL TRAMO ALCANZADO, que no retrocede.
 *
 * `computeProgression` lo saca de los puntos de ahora, y esos pueden bajar
 * —se desmarca un hábito, se borra una entrada—. Pero los hitos sellados solo
 * crecen (PRD 8.4), así que el tramo se calcula con el mayor de los puntos de
 * ahora y los del hito sellado más alto: si ya pisaste Salto, sigues en Salto.
 */
export function tramoAlcanzado(estado: {
  readonly totalPoints: number;
  readonly unlocked: readonly { readonly points: number }[];
}): Tramo {
  let puntos = estado.totalPoints;
  for (const hito of estado.unlocked) puntos = Math.max(puntos, hito.points);
  return tramoForPoints(puntos);
}

/** Su posición en la tira (0 = Paso). */
export function indiceDeTramo(key: string | null): number {
  return key === null ? -1 : TRAMOS.findIndex((t) => t.key === key);
}
